import { useHealthCheck } from '../../api/client'
import { Menu, Moon, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useThemeStore } from '../../stores/themeStore'
import { useTelemetryStore } from '../../stores/telemetryStore'
import { cn } from '../../lib/utils'
import type { SSEConnectionState } from '../../api/sse'
import { OrgSwitcher } from './OrgSwitcher'

// Connected pill colors per SSE state. The "connected" variant pulses on the
// dot — matching VoysiHeader (voysi-shell.jsx) and the marketing hero pill.
const sseStateMap: Record<
  SSEConnectionState,
  { labelKey: string; dot: string; pulse: boolean }
> = {
  connected: { labelKey: 'header.sse.connected', dot: 'bg-success', pulse: true },
  connecting: { labelKey: 'header.sse.connecting', dot: 'bg-warning', pulse: true },
  reconnecting: { labelKey: 'header.sse.reconnecting', dot: 'bg-warning', pulse: true },
  disconnected: { labelKey: 'header.sse.disconnected', dot: 'bg-error', pulse: false },
}

interface HeaderProps {
  onMenuToggle: () => void
}

export function Header({ onMenuToggle }: HeaderProps) {
  const { t } = useTranslation('common')
  const { data: health } = useHealthCheck()
  const { isDark, toggle } = useThemeStore()
  const sseConnectionState = useTelemetryStore((s) => s.sseConnectionState)
  const sseConfig = sseStateMap[sseConnectionState]

  return (
    <header className="flex h-[60px] flex-shrink-0 items-center justify-between gap-6 border-b border-ink-200 bg-white px-6 md:px-7">
      <button
        onClick={onMenuToggle}
        aria-label={t('header.openMenu')}
        className="rounded-lg p-2 text-text-secondary hover:bg-ink-100 hover:text-text-primary md:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>

      <div className="hidden md:block">
        <OrgSwitcher />
      </div>

      <div className="flex items-center gap-2">
        {/* Connected pill — mirrors VoysiHeader's pulse pill in voysi-shell.jsx. */}
        <div className="flex items-center gap-2 rounded-full border border-ink-200 px-3 py-1.5 text-xs text-ink-600">
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              sseConfig.dot,
              sseConfig.pulse && 'animate-[voysiPulse_1.4s_infinite]',
            )}
          />
          <span className="hidden sm:inline">{t(sseConfig.labelKey)}</span>
        </div>

        {/* API health — kept but compact. */}
        <div className="hidden items-center gap-2 sm:flex">
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              health?.status === 'ok' ? 'bg-success' : 'bg-error',
            )}
          />
          <span className="text-xs text-text-secondary">
            {health?.status === 'ok' ? t('header.api.ok') : t('header.api.down')}
          </span>
        </div>

        <button
          onClick={toggle}
          aria-label={t('header.toggleTheme')}
          className="rounded-lg p-2 text-text-secondary hover:bg-ink-100 hover:text-text-primary"
        >
          {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
      </div>
    </header>
  )
}
